import { useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { Box, Typography, Link, Alert, CircularProgress, Button } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { trpc } from '../../lib/trpc';

/**
 * Verify email page component
 * Handles email verification for notification channels using token from URL
 */
export function VerifyEmailPage() {
  const { token } = useParams<{ token: string }>();
  const [verificationAttempted, setVerificationAttempted] = useState(false);

  const verifyMutation = trpc.notifications.verify.useMutation();

  // Verify the email on page load
  useEffect(() => {
    if (token && !verificationAttempted) {
      setVerificationAttempted(true);
      verifyMutation.mutate({ token });
    }
  }, [token, verificationAttempted, verifyMutation]);

  // No token provided - show error
  if (!token) {
    return (
      <Box sx={{ textAlign: 'center' }}>
        <ErrorOutlineIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} aria-hidden="true" />
        <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
          Invalid Verification Link
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          This email verification link is invalid. Please check the link and try again.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <Button variant="contained" component={RouterLink} to="/login">
            Go to Login
          </Button>
          <Link
            component={RouterLink}
            to="/notifications"
            sx={{
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            Go to Notifications
          </Link>
        </Box>
      </Box>
    );
  }

  // Loading state - verification in progress
  if (verifyMutation.isPending) {
    return (
      <Box sx={{ textAlign: 'center' }}>
        <CircularProgress size={64} sx={{ mb: 3 }} />
        <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
          Verifying Email
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Please wait while we verify your email address...
        </Typography>
      </Box>
    );
  }

  // Error state - verification failed
  if (verifyMutation.isError || (verifyMutation.data && !verifyMutation.data.success)) {
    const errorMessage =
      verifyMutation.error?.message ||
      verifyMutation.data?.message ||
      'Failed to verify email. The link may be invalid or expired.';

    return (
      <Box sx={{ textAlign: 'center' }}>
        <ErrorOutlineIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} aria-hidden="true" />
        <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
          Verification Failed
        </Typography>
        <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
          {errorMessage}
        </Alert>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          To resend the verification email, please log in and visit your notification settings.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <Button variant="contained" component={RouterLink} to="/login">
            Go to Login
          </Button>
          <Link
            component={RouterLink}
            to="/notifications"
            sx={{
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <ArrowBackIcon fontSize="small" aria-hidden="true" />
            Go to Notifications
          </Link>
        </Box>
      </Box>
    );
  }

  // Success state - email verified
  if (verifyMutation.data?.success) {
    return (
      <Box sx={{ textAlign: 'center' }}>
        <CheckCircleOutlineIcon
          sx={{ fontSize: 64, color: 'success.main', mb: 2 }}
          aria-hidden="true"
        />
        <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
          Email Verified
        </Typography>
        <Alert severity="success" sx={{ mb: 3, textAlign: 'left' }}>
          {verifyMutation.data.message}
        </Alert>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Your email notification channel is now active and will receive alerts.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <Button variant="contained" component={RouterLink} to="/notifications">
            Go to Notifications
          </Button>
          <Link
            component={RouterLink}
            to="/login"
            sx={{
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <ArrowBackIcon fontSize="small" aria-hidden="true" />
            Go to Login
          </Link>
        </Box>
      </Box>
    );
  }

  // Default loading state (before mutation starts)
  return (
    <Box sx={{ textAlign: 'center' }}>
      <CircularProgress size={64} sx={{ mb: 3 }} />
      <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
        Verifying Email
      </Typography>
      <Typography variant="body1" color="text.secondary">
        Please wait while we verify your email address...
      </Typography>
    </Box>
  );
}

export default VerifyEmailPage;
